import React from 'react';
import { Button, Grid, Header3Text, Icon, IconSize, IconType, IDateInputValue } from '../../../../../ui-toolkit';
import { ISimpleDocument, IUploadDocument } from '../../../../../types/order/documents/upload/interfaces';
import { useDropzone } from 'react-dropzone';
import { SummaryItem } from '../../../../shared/Summary/SummaryItem/SummaryItem';
import { v4 as uuidv4 } from 'uuid';
import { UploadingDocumentInformation } from './UploadingDocumentInformation';
import { BottomSummaryItem, StyledCancelButton, StyledDropzoneArea, StyledGrid } from './UploadDocument.styles';
import { DateTime } from 'luxon';
import { IDocumentCategory } from 'types/order/documents/IDocumentCategoryType';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../../../API/documentCategories/DocumentCategoriesApi';

interface IUploadDocumentContainerProps {
  onUploadDocuments: (documents: IUploadDocument[]) => Promise<any>;
  onClose: () => void;
  onDrop: (files: File[]) => void;
  orderId: string;
  setUploadedDocuments: (documents: IDocumentInformation[]) => void;
  uploadedDocuments: IDocumentInformation[];
  categories: IDocumentCategory[];
}

export interface IDocumentInformation {
  file: File;
  category: IDocumentCategory | null;
  date?: IDateInputValue;
}

export const UploadDocumentContainer: React.FC<IUploadDocumentContainerProps> = ({
  onUploadDocuments,
  onClose,
  onDrop,
  orderId,
  setUploadedDocuments,
  uploadedDocuments,
  categories,
}) => {
  const { data: { documentCategoriesByName } = defaultNormalizedDocumentCategoriesData } =
    useListDocumentCategoriesQuery();

  const onSubmitClick = async () => {
    const documentsToLoad = uploadedDocuments.map((file: IDocumentInformation) =>
      file.date
        ? ({
            file: file.file,
            id: uuidv4(),
            order_id: orderId,
            category: file.category?.name,
            isRepeatable: file.category?.isRepeatable,
            metadata: {
              good_through_date: file.date.value ? DateTime.fromISO(file.date.value).toISO() : null,
            },
          } as IUploadDocument)
        : ({
            file: file.file,
            id: uuidv4(),
            order_id: orderId,
            category: file.category?.name,
            isRepeatable: file.category?.isRepeatable,
          } as ISimpleDocument)
    );
    await onUploadDocuments(documentsToLoad);

    onClose();
  };

  const onCategoryChange = (selectedCategory: IDocumentCategory, index: number) => {
    uploadedDocuments[index].category = selectedCategory;
    setUploadedDocuments([...uploadedDocuments]);
  };

  const onDateChange = (date: IDateInputValue, index: number) => {
    uploadedDocuments[index].date = date;
    setUploadedDocuments([...uploadedDocuments]);
  };

  const deleteDocument = (index: number) => {
    uploadedDocuments.splice(index, 1);
    setUploadedDocuments([...uploadedDocuments]);
  };

  const dateRequiringStandardDocuments = (selectedCategory: IDocumentCategory | null) =>
    selectedCategory !== null && documentCategoriesByName[selectedCategory.name]?.hasGoodThroughDate;

  const documentIsValid = (document: IDocumentInformation): boolean => {
    const dateValid = document.date !== undefined || !dateRequiringStandardDocuments(document.category);
    return dateValid && document.category !== null;
  };

  const onSubmitDisabled = () => {
    return uploadedDocuments.some((document: IDocumentInformation): boolean => !documentIsValid(document));
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: true });

  return (
    <>
      <SummaryItem label={''}>
        <StyledGrid {...getRootProps()}>
          <input {...getInputProps()} />
          <StyledDropzoneArea>
            <Icon type={IconType.Upload} size={IconSize.Large} />
            <Header3Text>Drag & Drop or click to browse</Header3Text>
          </StyledDropzoneArea>
        </StyledGrid>
        {uploadedDocuments.map((document, index) => (
          <StyledGrid key={document.file.lastModified}>
            <UploadingDocumentInformation
              onDeleteDocument={() => deleteDocument(index)}
              document={document}
              onCategoryChange={(category) => onCategoryChange(category, index)}
              onDateChange={(date) => onDateChange(date, index)}
              dateRequiringStandardDocuments={dateRequiringStandardDocuments}
            />
          </StyledGrid>
        ))}
      </SummaryItem>
      <BottomSummaryItem>
        <Grid columnCount={4} alignItems={'end'}>
          <StyledCancelButton intent={'ghost'} onClick={onClose}>
            Cancel
          </StyledCancelButton>
          <Button
            intent={'primary'}
            type={'button'}
            onClick={onSubmitDisabled() ? undefined : onSubmitClick}
            disabled={onSubmitDisabled()}
            data-test-id={'upload-documents-submit'}
          >
            Submit
          </Button>
        </Grid>
      </BottomSummaryItem>
    </>
  );
};
